import { useMemo } from 'react';

import { isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { SingleSelectOption } from '@/modules/common';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { RateSource } from '@/modules/common/types/rate-source';
import { ProjectStatus } from '@/modules/projects/types/project';

import { AssignmentModalProps } from '../../components/modals/assignment-modal/assignment-modal';
import { getEffectiveDateContext } from '../../components/modals/assignment-modal/helpers/assignment-modal-rates';
import { AssignmentStatus } from '../../types/assignment-status';

interface UseAssignmentDisplayStateParams {
  initialData: AssignmentModalProps['initialData'];
  rateSource: RateSource | undefined;
  currentCurrency: string;
  isBillable: boolean;
  committedDateFrom: Date | null | undefined;
  committedDateTo: Date | null | undefined;
  /** True when query 1 data is reused instead of query 2 */
  shouldReuseContractStartData: boolean;
  projectStatus: ProjectStatus | undefined;
  status: AssignmentStatus;
}

/**
 * Derives display-only state for the assignment modal:
 * initial dropdown items, alert visibility flags, and the effective date label.
 */
export function useAssignmentDisplayState({
  initialData,
  rateSource,
  currentCurrency,
  isBillable,
  committedDateFrom,
  committedDateTo,
  shouldReuseContractStartData,
  projectStatus,
  status,
}: UseAssignmentDisplayStateParams) {
  const { t } = useTranslation(['authenticated.planner']);

  // --- Initial dropdown items for edit mode ---

  const initialUserItem = useMemo((): DropdownSearchItem | undefined => {
    if (!initialData?.userId || !initialData?.userName) return undefined;
    return {
      id: String(initialData.userId),
      name: initialData.userName,
      avatarLink: initialData.userProfileImageUrl,
    };
  }, [initialData?.userId, initialData?.userName, initialData?.userProfileImageUrl]);

  const initialProjectItem = useMemo((): DropdownSearchItem | undefined => {
    if (!initialData?.projectId || !initialData?.projectLabel) return undefined;
    return {
      id: String(initialData.projectId),
      name: initialData.projectLabel,
      colorHex: initialData.projectColorHex,
    };
  }, [initialData?.projectId, initialData?.projectLabel, initialData?.projectColorHex]);

  const initialCurrencyItem = useMemo((): SingleSelectOption | undefined => {
    const code = currentCurrency || initialData?.currency;
    if (!code) return undefined;
    return { value: code, label: code };
  }, [currentCurrency, initialData?.currency]);

  // --- Alert flags ---

  const shouldShowProjectStatusAlert =
    projectStatus === ProjectStatus.Tentative && status === AssignmentStatus.Confirmed;

  const shouldShowRateSourceAlert =
    rateSource !== undefined && rateSource !== RateSource.ProjectBased;

  const hasRateSourceChanged = rateSource !== initialData?.rateSource;
  const isInitialRateNonProjectBased =
    !!initialData?.id &&
    initialData?.rateSource !== undefined &&
    initialData.rateSource !== RateSource.ProjectBased &&
    !hasRateSourceChanged;

  // --- Effective date label ---

  const effectiveDateLabel = useMemo(() => {
    if (isInitialRateNonProjectBased || !isBillable) return null;
    if (!rateSource) return null;
    if (committedDateFrom && committedDateTo && isSameDay(committedDateFrom, committedDateTo))
      return null;
    if (shouldReuseContractStartData) return t('form.effectiveDate.asOfStartDate');
    const context = getEffectiveDateContext(rateSource, committedDateFrom, committedDateTo);
    return context ? t(`form.effectiveDate.${context}`) : null;
  }, [
    isInitialRateNonProjectBased,
    isBillable,
    rateSource,
    shouldReuseContractStartData,
    committedDateFrom,
    committedDateTo,
    t,
  ]);

  return {
    initialUserItem,
    initialProjectItem,
    initialCurrencyItem,
    shouldShowProjectStatusAlert,
    shouldShowRateSourceAlert,
    isInitialRateNonProjectBased,
    effectiveDateLabel,
  };
}
